import { useCallback } from "react";
import { useFormHandler } from "shared/hooks";
import { z } from "zod";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import { IInviteStaffMember } from "shared/components/molecules";
import {
  ContactRole,
  EnumUserAccessOperatorsRole,
  UserAccessOperators,
  UserWithPictureTc,
} from "gql/generated";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Directory]);

export const inviteStaffMemberFormSchema = z
  .object({
    photo: z.string().optional(),
    email: z
      .string()
      .trim()
      .email({ message: i18n.t("core:FORM_ERRORS.INVALID_EMAIL") })
      .optional()
      .or(z.literal("")),
    firstName: z
      .string({
        required_error: `${i18n.t("core:STAFF.ADD_EDIT_PERSON.LABELS.FIRST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .trim()
      .min(1, {
        message: `${i18n.t("core:STAFF.ADD_EDIT_PERSON.LABELS.FIRST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      }),
    lastName: z
      .string({
        required_error: `${i18n.t("core:STAFF.ADD_EDIT_PERSON.LABELS.LAST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .trim()
      .min(1, {
        message: `${i18n.t("core:STAFF.ADD_EDIT_PERSON.LABELS.LAST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      }),
    title: z.string().trim().optional(),
    role: z.nativeEnum(EnumUserAccessOperatorsRole),
    contactRoles: z.array(z.nativeEnum(ContactRole)).default([]),
    sendInvitationEmail: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (!data.email && data.role !== EnumUserAccessOperatorsRole.NoAccess) {
      ctx.addIssue({
        message: i18n.t(
          "core:STAFF.ADD_EDIT_PERSON.MESSAGES.EMAIL_REQUIRED_FOR_ROLE",
        ),
        path: ["email"],
        code: "custom",
      });
    }
  });

export type InviteStaffMemberFormValues = z.infer<
  typeof inviteStaffMemberFormSchema
>;

type UseInviteStaffMemberFormProps = {
  user?: Partial<UserWithPictureTc>;
  userAccess: UserAccessOperators;
  sendInvitationEmail: boolean;
  dataProcessor: (personToAdd: IInviteStaffMember) => Promise<void>;
  onSuccess: () => void;
  onError: (message?: string) => void;
};

export const useInviteStaffMemberForm = ({
  user,
  userAccess,
  sendInvitationEmail = false,
  dataProcessor,
  onSuccess,
  onError,
}: UseInviteStaffMemberFormProps) => {
  const defaultValues: InviteStaffMemberFormValues = {
    photo: user?.photo?._id,
    email: user?.email ?? "",
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    title: userAccess?.title ?? user?.title ?? "",
    role:
      // Transform the OLD STAFF role to the new Editor role
      userAccess?.role === EnumUserAccessOperatorsRole.Staff
        ? EnumUserAccessOperatorsRole.Editor
        : (userAccess?.role ?? EnumUserAccessOperatorsRole.Editor),
    contactRoles: (userAccess?.contactRoles ?? []).filter(
      Boolean,
    ) as unknown as ContactRole[],
    sendInvitationEmail,
  };

  return useFormHandler<InviteStaffMemberFormValues>(
    useCallback(
      async data => {
        if (data) {
          // Ensure they aren't trying to clear the email address
          // All users will at least have a `wademo.hidden-...` email address
          if (!data.email && user?.email) {
            data.email = user.email;
          }
          await dataProcessor(data as IInviteStaffMember)
            .then(() => onSuccess())
            .catch(() =>
              onError(
                `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.FORM_ERRORS.CREATE_USER_INVITE")} ${i18n.t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
              ),
            );
        }
      },
      [dataProcessor, onError, onSuccess, user],
    ),
    defaultValues,
    { schema: inviteStaffMemberFormSchema, mode: "onBlur" },
  );
};
